import {
  sendAnalytics,
  PlaycastAnalyticsRole,
  PlaycastAnalyticsPlayerInviteViewed,
  PlaycastAnalyticsPlayerInviteAccepted,
  PlaycastAnalyticsPlayerRoleChanged,
  PlaycastAnalyticsPlayerFrameratePanicked,
  PlaycastAnalyticsWebRtcStatsGathered,
} from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from './api';
import { PeerQualityRecommendation, PlaycastWebrtcQualityRawStats } from '@playcastdotio/CoreTypes';
import { joinIdState, playerNameState } from './api/stores/lobbyStores';
import { playerConnectionState, truncMachineIdState } from './api/stores/playerStores';
import { invitationState } from './api/stores/lobbyStores';
import { authStore, joinState } from './api/stores/globalStores';

const persistentId = getPersistentPlayerId();

// PLAYER METRICS

// This is the player authentication metric
authStore.addListener((state, previousState) => {
  if (state.playerAuthId && state.playerAuthId !== previousState?.playerAuthId) {
    sendAnalytics({
      name: 'User Authenticated',
      role: 'player',
      playerAuthId: state.playerAuthId,
      persistentId,
      source: 'browser',
    });
  }
});

export const sendMetricPlayerVisitInvite = () => {
  const playerAuthId = authStore.currentState.playerAuthId;
  const inviteId = invitationState.currentState.selectedInvite?.id ?? '';

  sendAnalytics({
    name: 'Invite Viewed',
    role: 'player',
    playerAuthId,
    persistentId,
    inviteId,
  } as PlaycastAnalyticsPlayerInviteViewed);
};

let playerName = playerNameState.currentState.name;
playerNameState.addListener((state) => {
  playerName = state.name;
});

export const sendMetricPlayerAcceptInvite = () => {
  const inviteId = invitationState.currentState.selectedInvite?.id ?? '';
  const playerAuthId = authStore.currentState.playerAuthId;

  sendAnalytics({
    name: 'Invite Accepted',
    role: 'player',
    playerAuthId,
    persistentId,
    playerName,
    inviteId,
  } as PlaycastAnalyticsPlayerInviteAccepted);
};

export const initConnectionListeners = () => {
  joinState.addListener((state, previousState) => {
    if (state.state === 'chooseInvite' && state.state !== previousState?.state) {
      sendMetricPlayerVisitInvite();
    }
    if (state.state === 'connecting' && state.state !== previousState?.state) {
      sendMetricPlayerAcceptInvite();
    }
  });
};

export const sendMetricPlayerChangeRole = (role: PlaycastAnalyticsRole) => {
  const broadcastSessionId = playerConnectionState.currentState.broadcastSessionId;
  const playerAuthId = authStore.currentState.playerAuthId ?? '';
  const inviteId = invitationState.currentState.selectedInvite?.id ?? '';
  const playerConnectionId: string = playerConnectionState.currentState.playerPeerId ?? '';

  sendAnalytics({
    name: 'Player Role Changed',
    role: 'player',
    playerAuthId,
    persistentId,
    playerConnectionId,
    playerName,
    broadcastSessionId,
    inviteId,
  } as PlaycastAnalyticsPlayerRoleChanged);
};

export const sendMetricPlayerFrameratePanic = (hardReset: boolean, reason: 'fps' | 'bitrate') => {
  const broadcastSessionId = playerConnectionState.currentState.broadcastSessionId;
  const playerAuthId = authStore.currentState.playerAuthId ?? '';
  const inviteId = invitationState.currentState.selectedInvite?.id ?? '';
  const playerConnectionId: string = playerConnectionState.currentState.playerPeerId ?? '';

  sendAnalytics({
    name: 'Player Framerate Panicked',
    role: 'system',
    broadcastSessionId,
    playerAuthId,
    persistentId,
    playerConnectionId,
    inviteId,
    hardReset,
    reason,
  } as PlaycastAnalyticsPlayerFrameratePanicked);
};

export type WebrtcQualityStatsMetric = {
  recommendation: PeerQualityRecommendation;
  reason: string;
  rawStats: PlaycastWebrtcQualityRawStats;
};

export const sendMetricPlayerBatchWebrtcStats = async (stats: WebrtcQualityStatsMetric[], interval: number) => {
  // sanitize the stats so we dont have any null, undefined, or NaN values.
  const sanitizedStats = stats.map((stat) => {
    const sanitizedRawStats = { ...stat.rawStats };
    Object.keys(sanitizedRawStats).forEach((key) => {
      if (typeof sanitizedRawStats[key] === 'number' && isNaN(sanitizedRawStats[key])) {
        sanitizedRawStats[key] = 0;
      }
      if (typeof sanitizedRawStats[key] === 'object' && sanitizedRawStats[key] !== null) {
        Object.keys(sanitizedRawStats[key]).forEach((subKey) => {
          if (typeof sanitizedRawStats[key][subKey] === 'number' && isNaN(sanitizedRawStats[key][subKey])) {
            sanitizedRawStats[key][subKey] = 0;
          }
        });
      }
      if (Array.isArray(sanitizedRawStats[key])) {
        sanitizedRawStats[key] = sanitizedRawStats[key].map((val) => {
          if (typeof val === 'number' && isNaN(val)) {
            return 0;
          }
          return val;
        });
      }
    });
    return {
      ...stat,
      rawStats: sanitizedRawStats,
    };
  });

  const broadcastSessionId = playerConnectionState.currentState.broadcastSessionId;
  const playerAuthId = authStore.currentState.playerAuthId ?? '';
  const inviteId = invitationState.currentState.selectedInvite?.id ?? '';
  const playerConnectionId: string = playerConnectionState.currentState.playerPeerId ?? '';

  sendAnalytics({
    name: 'WebRTC Stats Gathered',
    role: 'system',
    tier1Filters: ['bigquery-only'],
    broadcastSessionId,
    playerAuthId,
    persistentId,
    playerConnectionId,
    inviteId,
    qualityStats: sanitizedStats,
    collectionInterval: interval,
    hostMachineId: truncMachineIdState.currentState || undefined,
  } as PlaycastAnalyticsWebRtcStatsGathered);
};
